/**
 * RealEstatePostPage — /realestate/post
 * Homeowner listing form. Photos up to 10 (URL only for MVP).
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PROP_CATEGORIES, propLabel } from "@/data/propertyCategories";
import { Home, ArrowLeft, Loader2, Send, AlertTriangle, Plus, X, Sparkles } from "lucide-react";

const HOMEOWNER_CONSENT = (
  "I confirm that I have the legal right to list this property, that the " +
  "information I've provided is accurate to the best of my knowledge, and " +
  "that I will not use Social Pulse Real Estate for scams, misleading " +
  "listings, or discriminatory practices."
);

const MAX_PHOTOS = 10;

function Row({ label, hint, required, testid, children }) {
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">{label}{required && " *"}</div>
      {children}
      {hint && <div className="text-[10px] opacity-60 mt-1">{hint}</div>}
    </div>
  );
}

export default function RealEstatePostPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", category: "", sale_or_rent: "sale", price: "", currency: "USD", price_period: "",
    city: user?.city || "", state: user?.state || "", country: user?.country || "",
    address: "", description: "",
    bedrooms: "", bathrooms: "", parking: "", garden: false, pool: false, balcony: false, furnished: false, ac: false,
    size_value: "", size_unit: "sqft",
    contact_via: "socialpulse", contact_value: "",
  });
  const [photos, setPhotos] = useState([""]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = useMemo(() => (
    form.title.trim().length >= 3 &&
    form.category &&
    form.sale_or_rent &&
    form.description.trim().length >= 10 &&
    (form.city || form.state || form.country || form.address) &&
    consent
  ), [form, consent]);

  const submit = async () => {
    if (!canSubmit) return toast.error("Please complete all required fields.");
    setBusy(true);
    try {
      const cleanPhotos = photos.map((s) => s.trim()).filter(Boolean).slice(0, MAX_PHOTOS);
      const payload = {
        title: form.title.trim(),
        category: form.category,
        poster_type: "homeowner",
        sale_or_rent: form.sale_or_rent,
        price: form.price ? Number(form.price) : null,
        price_period: form.sale_or_rent === "rent" ? (form.price_period || "per-month") : null,
        currency: form.currency,
        address: form.address || null,
        city: form.city, state: form.state, country: form.country,
        description: form.description,
        features: {
          bedrooms:  form.bedrooms  === "" ? null : Number(form.bedrooms),
          bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
          parking:   form.parking   === "" ? null : Number(form.parking),
          garden: form.garden, pool: form.pool, balcony: form.balcony,
          furnished: form.furnished, ac: form.ac,
        },
        size_value: form.size_value === "" ? null : Number(form.size_value),
        size_unit: form.size_unit,
        photos: cleanPhotos,
        contact_via: form.contact_via,
        contact_value: form.contact_value || null,
        consent_agreed: consent,
      };
      const { data } = await http.post("/properties", payload);
      toast.success("Property listed!");
      nav(`/realestate/${data.property.slug || data.property.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed."); }
    finally { setBusy(false); }
  };

  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="re-post-signin">
      <Home className="w-10 h-10 mx-auto opacity-40 mb-2" />
      <h1 className="font-display text-2xl mb-2">Sign in to list a property</h1>
      <Link to="/login?redirect=/realestate/post" className="wofy-btn-primary inline-flex">Sign in</Link>
    </div>
  );

  const updatePhoto = (i, v) => setPhotos(photos.map((p, idx) => idx === i ? v : p));
  const addPhoto = () => photos.length < MAX_PHOTOS && setPhotos([...photos, ""]);
  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="re-post-page">
      <Link to="/realestate" className="text-xs opacity-70 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> All listings
      </Link>
      <h1 className="font-display text-4xl mb-6">List your property</h1>

      <Row label="Title" required testid="re-title">
        <input value={form.title} onChange={(e) => set("title", e.target.value)} className="wofy-input" placeholder="3-bedroom family home in Kochi" data-testid="re-title-input" />
      </Row>

      <div className="grid md:grid-cols-2 gap-3">
        <Row label="Property type" required testid="re-cat">
          <select value={form.category} onChange={(e) => set("category", e.target.value)} className="wofy-input" data-testid="re-cat-select">
            <option value="">— select —</option>
            {Object.entries(PROP_CATEGORIES).map(([gid, g]) => (
              <optgroup key={gid} label={`${g.emoji}  ${g.label}`}>
                {g.subs.map((s) => <option key={`${gid}-${s}`} value={s}>{propLabel(s)}</option>)}
              </optgroup>
            ))}
          </select>
        </Row>
        <Row label="Sale or rent" required testid="re-sor">
          <select value={form.sale_or_rent} onChange={(e) => set("sale_or_rent", e.target.value)} className="wofy-input" data-testid="re-sor-select">
            <option value="sale">For sale</option>
            <option value="rent">For rent</option>
          </select>
        </Row>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <Row label="Price" testid="re-price">
          <input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="wofy-input" placeholder="0" data-testid="re-price-input" />
        </Row>
        <Row label="Currency" testid="re-currency">
          <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className="wofy-input" data-testid="re-currency-select">
            {["USD","EUR","GBP","INR","AED","AUD","CAD"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Row>
        {form.sale_or_rent === "rent" && (
          <Row label="Rent period" testid="re-period">
            <select value={form.price_period} onChange={(e) => set("price_period", e.target.value)} className="wofy-input">
              <option value="per-month">per month</option>
              <option value="per-week">per week</option>
              <option value="per-night">per night</option>
              <option value="per-year">per year</option>
            </select>
          </Row>
        )}
      </div>

      <Row label="Address" testid="re-address">
        <input value={form.address} onChange={(e) => set("address", e.target.value)} className="wofy-input" placeholder="Street, area, landmarks" data-testid="re-address-input" />
      </Row>
      <div className="grid md:grid-cols-3 gap-3">
        <Row label="City" testid="re-city"><input value={form.city} onChange={(e) => set("city", e.target.value)} className="wofy-input" data-testid="re-city-input" /></Row>
        <Row label="State"  testid="re-state"><input value={form.state} onChange={(e) => set("state", e.target.value)} className="wofy-input" data-testid="re-state-input" /></Row>
        <Row label="Country" testid="re-country"><input value={form.country} onChange={(e) => set("country", e.target.value)} className="wofy-input" data-testid="re-country-input" /></Row>
      </div>

      <Row label="Description" required hint="What makes this property special?" testid="re-desc">
        <textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className="wofy-input" data-testid="re-desc-input" />
      </Row>

      <div className="grid md:grid-cols-3 gap-3">
        <Row label="Bedrooms"  testid="re-bed"><input type="number" min="0" value={form.bedrooms}  onChange={(e) => set("bedrooms", e.target.value)}  className="wofy-input" data-testid="re-bed-input" /></Row>
        <Row label="Bathrooms" testid="re-bath"><input type="number" min="0" step="0.5" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} className="wofy-input" data-testid="re-bath-input" /></Row>
        <Row label="Parking"   testid="re-park"><input type="number" min="0" value={form.parking}   onChange={(e) => set("parking", e.target.value)}   className="wofy-input" data-testid="re-park-input" /></Row>
      </div>

      <Row label="Features" testid="re-features">
        <div className="flex flex-wrap gap-3 text-sm">
          {[
            ["garden", "🌳 Garden"], ["pool", "🏊 Pool"], ["balcony", "🌇 Balcony"],
            ["furnished", "🛋️ Furnished"], ["ac", "❄️ Air conditioning"],
          ].map(([k, label]) => (
            <label key={k} className="inline-flex items-center gap-1.5 wofy-card !py-1.5 !px-3 cursor-pointer" style={{ background: form[k] ? "var(--wofy-primary)10" : undefined }}>
              <input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`re-feat-${k}`} /> {label}
            </label>
          ))}
        </div>
      </Row>

      <div className="grid md:grid-cols-2 gap-3">
        <Row label="Size" testid="re-size">
          <div className="flex gap-2">
            <input type="number" min="0" value={form.size_value} onChange={(e) => set("size_value", e.target.value)} className="wofy-input flex-1" data-testid="re-size-input" placeholder="1200" />
            <select value={form.size_unit} onChange={(e) => set("size_unit", e.target.value)} className="wofy-input w-28">
              <option value="sqft">sqft</option><option value="sqm">sqm</option><option value="acres">acres</option>
            </select>
          </div>
        </Row>
      </div>

      <Row label={`Photos (URL) — up to ${MAX_PHOTOS} · free plan`} hint="Paid plans lift this cap. Paste image URLs for now." testid="re-photos">
        <div className="space-y-2">
          {photos.map((p, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input value={p} onChange={(e) => updatePhoto(i, e.target.value)} className="wofy-input flex-1" placeholder="https://…" data-testid={`re-photo-${i}`} />
              {photos.length > 1 && <button onClick={() => removePhoto(i)} className="text-red-600" data-testid={`re-photo-remove-${i}`}><X className="w-4 h-4" /></button>}
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <button onClick={addPhoto} className="text-xs underline opacity-80" data-testid="re-photo-add"><Plus className="w-3 h-3 inline" /> Add another photo</button>
          )}
          <div className="text-[10px] opacity-60 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Want more than 10 photos, video tour, and featured placement? Upgrade to a paid listing.</div>
        </div>
      </Row>

      <div className="grid md:grid-cols-2 gap-3">
        <Row label="Contact via" testid="re-contact-via">
          <select value={form.contact_via} onChange={(e) => set("contact_via", e.target.value)} className="wofy-input" data-testid="re-contact-via-select">
            <option value="socialpulse">Social Pulse messages</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
          </select>
        </Row>
        <Row label={form.contact_via === "socialpulse" ? "— nothing needed" : "Address or number"} testid="re-contact-value">
          <input value={form.contact_value} onChange={(e) => set("contact_value", e.target.value)} className="wofy-input" disabled={form.contact_via === "socialpulse"} data-testid="re-contact-value-input" />
        </Row>
      </div>

      <div className="rounded-lg border p-4 space-y-2 bg-amber-50/60 mb-4" style={{ borderColor: "#F59E0B" }} data-testid="re-consent-block">
        <div className="inline-flex items-center gap-1.5 font-semibold text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4" /> Homeowner authorization
        </div>
        <p className="text-xs text-amber-950/90 leading-relaxed">{HOMEOWNER_CONSENT}</p>
        <label className="inline-flex items-start gap-2 text-xs pt-1">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="re-consent-checkbox" className="mt-0.5" />
          <span>I confirm the statements above.</span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-4 border-t" style={{ borderColor: "var(--wofy-border)" }}>
        <Link to="/realestate" className="wofy-btn-ghost">Cancel</Link>
        <button onClick={submit} disabled={!canSubmit || busy} className="wofy-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="re-publish">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Publish listing
        </button>
      </div>
    </div>
  );
}
